import type { ExamId, IsoDateTime, QuestionId } from '../core/model/brands.ts';
import {
  recordAttempts,
  type AttemptHistory,
  type ExamHistory,
  type QuestionAttempts,
} from '../core/model/history.ts';
import type { Outcome } from '../core/model/session.ts';
import { read, remove, write } from './store.ts';

/**
 * The attempt history, on this device.
 *
 * What it holds, in full: for each exam id, for each question id, how many times that question has
 * been scored, how many of those were wrong, the last outcome and when. No question text, no
 * answers, no exam file, no name. Someone reading this store learns which numbered questions of
 * which exam ids a person finds hard, and nothing else.
 */

const OUTCOMES: readonly Outcome[] = ['correct', 'incorrect', 'partial', 'unanswered', 'ungraded'];

export function loadHistory(): AttemptHistory {
  return read('history', {}, revive);
}

export function historyFor(examId: ExamId): ExamHistory {
  return loadHistory()[examId] ?? {};
}

/** Folds a finished session in. Returns false if the device refused to store it. */
export function recordSession(
  examId: ExamId,
  outcomes: readonly { readonly questionId: QuestionId; readonly outcome: Outcome }[],
  at: IsoDateTime,
): boolean {
  const history = loadHistory();
  return write('history', {
    ...history,
    [examId]: recordAttempts(history[examId] ?? {}, outcomes, at),
  });
}

export function clearHistory(): void {
  remove('history');
}

/** Total questions tracked, so the interface can say what forgetting would cost. */
export function historySize(history: AttemptHistory): { exams: number; questions: number } {
  const exams = Object.keys(history);
  return {
    exams: exams.length,
    questions: exams.reduce((total, examId) => total + Object.keys(history[examId] ?? {}).length, 0),
  };
}

/**
 * Rebuilds a history from stored JSON, discarding anything malformed.
 *
 * Written defensively for the same reason the importer is: this is data from outside the running
 * program. A corrupt entry drops out; it never becomes a negative count that then filters an exam
 * down to nothing for reasons the user cannot see.
 */
function revive(value: unknown): AttemptHistory | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const out: Record<string, Record<string, QuestionAttempts>> = {};

  for (const [examId, questions] of Object.entries(value as Record<string, unknown>)) {
    if (typeof questions !== 'object' || questions === null || Array.isArray(questions)) {
      continue;
    }
    const kept: Record<string, QuestionAttempts> = {};

    for (const [questionId, attempts] of Object.entries(questions as Record<string, unknown>)) {
      const entry = reviveAttempts(attempts);
      if (entry !== null) {
        kept[questionId] = entry;
      }
    }
    if (Object.keys(kept).length > 0) {
      out[examId] = kept;
    }
  }

  return out;
}

function reviveAttempts(value: unknown): QuestionAttempts | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const candidate = value as Partial<Record<keyof QuestionAttempts, unknown>>;
  const seen = candidate.seen;
  const incorrect = candidate.incorrect;
  const lastOutcome = candidate.lastOutcome;
  const lastAt = candidate.lastAt;

  if (
    typeof seen !== 'number' ||
    typeof incorrect !== 'number' ||
    !Number.isFinite(seen) ||
    !Number.isFinite(incorrect) ||
    seen < 0 ||
    incorrect < 0 ||
    incorrect > seen ||
    typeof lastAt !== 'string' ||
    !OUTCOMES.includes(lastOutcome as Outcome)
  ) {
    return null;
  }

  return {
    seen: Math.floor(seen),
    incorrect: Math.floor(incorrect),
    lastOutcome: lastOutcome as Outcome,
    lastAt: lastAt as IsoDateTime,
  };
}
